import LeftOutlined from '@ant-design/icons/lib/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/lib/icons/RightOutlined';
import { ConfigProvider, Divider, Flex, Form } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { putQuestionnaire } from '@/apis';
import BgAgriculture from '@/assets/images/bg-Agriculture.png';
import BgAnimalHusbandry from '@/assets/images/bg-AnimalHusbandry.png';
import BgCrafts from '@/assets/images/bg-CraftsAndAgriculturalProcessing.png';
import BgDevelopment from '@/assets/images/bg-DevelopmentAssociationAndCommunity.png';
import BgExperience from '@/assets/images/bg-ExperienceActivitiesAndSightseeing.png';
import BgRestaurant from '@/assets/images/bg-Restaurant.png';
import BgTourism from '@/assets/images/bg-TourismAndAccommodation.png';
import companyLogo from '@/assets/images/company-logo.png';
import unionLogo from '@/assets/images/union-logo.png';
import Logo from '@/components/Logo';
import OutlinedButton from '@/components/OutlinedButton/OutlinedButton';
import ProgressBar from '@/components/ProgressBar';
import {
  HamburgerMenu,
  ScoreRadioGroup,
  TextArea,
} from '@/components/Questionniare';
import { AnswerType, Industry, QuestionModule } from '@/enums';
import { useApiHandler, useBreakpoint, useDebounce } from '@/hooks';
import { QuestionWithAnswer } from '@/types';
import { formatQuestionNumber } from '@/utils/common';
import {
  getIndustryBackgroundGradient,
  getIndustryButtonGradient,
  getIndustryScoreColor,
} from '@/utils/industryColors';

import * as Style from './SDGsModule.style';

interface SDGsModuleProps {
  questions: QuestionWithAnswer[];
  currentQuestionIndex: number;
  industry?: Industry;
  onCrossModuleNavigation: (direction: 'prev' | 'next') => Promise<void>;
}

const ANSWER_OPTIONS = [
  { key: '0', label: <>完全沒有做到</>, value: '0' },
  { key: '1', label: <>有在關注此項</>, value: '1' },
  {
    key: '2',
    label: (
      <>
        原本業務
        <br />
        符合此項
      </>
    ),
    value: '2',
  },
  {
    key: '3',
    label: (
      <>
        已有順暢的模式
        <br />
        在運行中
      </>
    ),
    value: '3',
  },
  {
    key: '4',
    label: (
      <>
        開發符合此項
        <br />
        的商業模式
      </>
    ),
    value: '4',
  },
  {
    key: '5',
    label: (
      <>
        積極實施並且
        <br />
        影響他人
      </>
    ),
    value: '5',
  },
];

const SDGsModule = ({
  questions,
  currentQuestionIndex: initialQuestionIndex,
  industry = Industry.Agriculture,
  onCrossModuleNavigation,
}: SDGsModuleProps) => {
  const [form] = Form.useForm();
  const [answersMap, setAnswersMap] = useState<Map<string, string>>(new Map());
  const [currentQuestionIndex, setCurrentQuestionIndex] =
    useState(initialQuestionIndex);
  const [currentQuestion, setCurrentQuestion] =
    useState<QuestionWithAnswer | null>(null);
  const [mounted, setMounted] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Breakpoint detection for responsive rendering
  const { isMobileOrTablet } = useBreakpoint();

  const { questionnaireId } = useParams<{ questionnaireId: string }>();

  const { handleApiCall } = useApiHandler();

  const getBackgroundImage = (industry: Industry) => {
    const industryImages = {
      [Industry.CraftsAndAgriculturalProcessing]: BgCrafts,
      [Industry.Agriculture]: BgAgriculture,
      [Industry.DevelopmentAssociationAndCommunity]: BgDevelopment,
      [Industry.ExperienceActivitiesAndSightseeing]: BgExperience,
      [Industry.Restaurant]: BgRestaurant,
      [Industry.TourismAndAccommodation]: BgTourism,
      [Industry.AnimalHusbandry]: BgAnimalHusbandry,
    };
    return industryImages[industry];
  };

  const getDisplayQuestionNumber = (
    currentQuestion: QuestionWithAnswer,
    questions: QuestionWithAnswer[],
  ): string => {
    if (currentQuestion.level === 1)
      return formatQuestionNumber(currentQuestion.order);

    const parentQuestion = questions.find(
      (q) => q.id === currentQuestion.parentQuestionId,
    );
    return formatQuestionNumber(parentQuestion?.order ?? currentQuestion.order);
  };

  const shouldShowChildQuestion = (
    question: QuestionWithAnswer,
    allQuestions: QuestionWithAnswer[],
    answersMap: Map<string, string>,
  ): boolean => {
    if (question.level <= 1) return true;

    const parent = question.parentQuestionId
      ? allQuestions.find((q) => q.id === question.parentQuestionId)
      : allQuestions.find((q) => q.level === question.level - 1);

    if (!parent) return true;
    return ['3', '4', '5'].includes(answersMap.get(parent.id) || '');
  };

  const saveAnswerOnly = async (
    questionId: string,
    answer: string,
    industryId?: string,
  ): Promise<string[]> => {
    try {
      const response = await handleApiCall(
        () =>
          putQuestionnaire(
            {
              questionnaireId: questionnaireId!,
              questionId,
              answer,
              ...(industryId ? { industryId } : {}),
            },
            false,
          ),
        { showLoading: false },
      );

      // 檢查後端是否返回了被清除的子問題ID列表
      if (
        response &&
        'clearedChildQuestionIds' in response &&
        Array.isArray(response.clearedChildQuestionIds)
      ) {
        return response.clearedChildQuestionIds;
      }

      return [];
    } catch (error) {
      console.error('Failed to save answer:', error);
      return [];
    }
  };

  const handleBack = async () => {
    if (isNavigating) return;

    setIsNavigating(true);

    try {
      let prevIndex = currentQuestionIndex - 1;
      let prevQuestion: QuestionWithAnswer | null = null;

      while (prevIndex >= 0) {
        const candidateQuestion = questions[prevIndex];
        if (candidateQuestion) {
          if (candidateQuestion.level > 1) {
            if (
              shouldShowChildQuestion(candidateQuestion, questions, answersMap)
            ) {
              prevQuestion = candidateQuestion;
              break;
            }
          } else {
            prevQuestion = candidateQuestion;
            break;
          }
        }
        prevIndex--;
      }

      if (prevQuestion) {
        await handleApiCall(
          () =>
            putQuestionnaire(
              {
                questionnaireId: questionnaireId!,
                currentQuestionId: prevQuestion.id,
              },
              false,
            ),
          { showLoading: false },
        );

        setCurrentQuestionIndex(prevIndex);
        setCurrentQuestion(prevQuestion);
      } else {
        await onCrossModuleNavigation('prev');
      }
    } finally {
      setIsNavigating(false);
    }
  };

  const handleNext = async () => {
    if (!currentQuestion) return;
    if (isNavigating) return;

    setIsNavigating(true);

    try {
      let nextIndex = currentQuestionIndex + 1;
      let nextQuestion: QuestionWithAnswer | null = null;

      while (nextIndex < questions.length) {
        const candidateQuestion = questions[nextIndex];
        if (candidateQuestion) {
          if (candidateQuestion.level > 1) {
            if (
              shouldShowChildQuestion(candidateQuestion, questions, answersMap)
            ) {
              nextQuestion = candidateQuestion;
              break;
            }
          } else {
            nextQuestion = candidateQuestion;
            break;
          }
        }
        nextIndex++;
      }

      if (!nextQuestion) {
        if (currentQuestion.answerType === AnswerType.Info) {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    questionId: currentQuestion.id,
                    answer: 'true',
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to save info question answer:', error);
            return;
          }
        } else {
          try {
            await form.validateFields();
          } catch {
            return;
          }
          const currentAnswer = form.getFieldValue(currentQuestion.id);
          if (currentAnswer != null && currentAnswer !== '') {
            try {
              await handleApiCall(
                () =>
                  putQuestionnaire(
                    {
                      questionnaireId: questionnaireId!,
                      questionId: currentQuestion.id,
                      answer: currentAnswer.toString(),
                    },
                    false,
                  ),
                { showLoading: false },
              );
            } catch (error) {
              console.error('Failed to save answer:', error);
              return;
            }
          }
        }

        // Call parent handler to navigate to next module (will trigger generation UI)
        await onCrossModuleNavigation('next');
        return;
      }

      if (currentQuestion.answerType === AnswerType.Info) {
        try {
          await handleApiCall(
            () =>
              putQuestionnaire(
                {
                  questionnaireId: questionnaireId!,
                  questionId: currentQuestion.id,
                  answer: 'true',
                  currentQuestionId: nextQuestion.id,
                },
                false,
              ),
            { showLoading: false },
          );
        } catch (error) {
          console.error('Failed to save info question answer:', error);
          return;
        }
      } else {
        try {
          await form.validateFields();
        } catch {
          return;
        }
        const currentAnswer = form.getFieldValue(currentQuestion.id);
        if (currentAnswer != null && currentAnswer !== '') {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    questionId: currentQuestion.id,
                    answer: currentAnswer.toString(),
                    currentQuestionId: nextQuestion.id,
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to save answer and update position:', error);
            return;
          }
        } else {
          try {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    currentQuestionId: nextQuestion.id,
                  },
                  false,
                ),
              { showLoading: false },
            );
          } catch (error) {
            console.error('Failed to update position:', error);
            return;
          }
        }
      }

      setCurrentQuestionIndex(nextIndex);
      setCurrentQuestion(nextQuestion);
    } finally {
      setIsNavigating(false);
    }
  };

  // 為 textarea 創建一個包裝函數來處理被清除的子問題
  const debouncedSaveAnswerWrapper = useDebounce(
    async (questionId: string, answer: string, industryId?: string) => {
      const clearedIds = await saveAnswerOnly(questionId, answer, industryId);

      // 如果有被清除的子問題，更新前端狀態
      if (clearedIds.length > 0) {
        const fieldsToUpdate: Record<string, string> = {};
        clearedIds.forEach((childQuestionId: string) => {
          fieldsToUpdate[childQuestionId] = '';
          setAnswersMap((prev) => {
            const newMap = new Map(prev);
            newMap.delete(childQuestionId);
            return newMap;
          });
        });
        form.setFieldsValue(fieldsToUpdate);
      }
    },
    300,
  );

  useEffect(() => {
    if (questions.length > 0) {
      const newAnswersMap = new Map<string, string>();
      const formValues: Record<string, string> = {};

      questions.forEach((q) => {
        if (q.answer) {
          formValues[q.id] = q.answer;
          newAnswersMap.set(q.id, q.answer);
        }
      });

      setAnswersMap(newAnswersMap);

      form.setFieldsValue(formValues);

      setCurrentQuestionIndex(initialQuestionIndex);
      setCurrentQuestion(questions[initialQuestionIndex] || null);
      setMounted(true);
    }
  }, [questions, initialQuestionIndex, form]);

  useEffect(() => {
    if (currentQuestion) {
      const value = answersMap.get(currentQuestion.id) ?? '';
      const currentValue = form.getFieldValue(currentQuestion.id);
      if (value !== currentValue) {
        form.setFieldValue(currentQuestion.id, value);
      }
    }
  }, [currentQuestion, answersMap, form]);

  // Info page rendering (mobile/tablet version)
  if (isMobileOrTablet && currentQuestion?.answerType === AnswerType.Info) {
    return (
      <Style.BeforeSDGsStageContainer $bgImage={getBackgroundImage(industry)}>
        <Style.TopSection vertical>
          <Style.InfoTitle>
            SDGs自我評估問卷表
            <br />
            {industry}
          </Style.InfoTitle>

          <Style.ButtonContainer gap="16px">
            <OutlinedButton width="18rem" height="4rem" onClick={handleNext}>
              START THE QUESTIONNAIRE
            </OutlinedButton>
          </Style.ButtonContainer>
        </Style.TopSection>

        <ConfigProvider
          theme={{
            token: {
              colorSplit: '#f2f2f2',
            },
          }}
        >
          <Divider size="large" />
        </ConfigProvider>

        <Style.BottomSection justify="space-between" align="end">
          <Style.InfoLogoContainer vertical gap="16px">
            <Logo src={companyLogo} alt="Company Logo" height="40px" />
            <Logo src={unionLogo} alt="Union Logo" height="40px" />
          </Style.InfoLogoContainer>

          <Flex vertical gap={24}>
            <Style.AnswerExampleTitle>
              每個問題都使用0-5分制，其中：
            </Style.AnswerExampleTitle>
            <Flex vertical gap={8}>
              {ANSWER_OPTIONS.map((option) => (
                <Style.MobileAnswerWrapper key={option.key}>
                  <Style.AnswerExampleRadio>
                    {option.value}
                  </Style.AnswerExampleRadio>
                  <Style.AnswerExampleText>
                    {option.label}
                  </Style.AnswerExampleText>
                </Style.MobileAnswerWrapper>
              ))}
            </Flex>
          </Flex>
        </Style.BottomSection>
      </Style.BeforeSDGsStageContainer>
    );
  }

  // Questionnaire page rendering (mobile/tablet version)
  if (isMobileOrTablet && currentQuestion && mounted) {
    return (
      <Style.MobileContainer
        $gradient={getIndustryBackgroundGradient(industry)}
      >
        <HamburgerMenu
          questions={questions}
          answersMap={answersMap}
          module={QuestionModule.SDGs}
          industry={industry}
          currentStep={
            currentQuestion.answerType !== AnswerType.Info
              ? questions
                  .slice(0, currentQuestionIndex + 1)
                  .filter((q) => q.answerType !== AnswerType.Info).length - 1
              : undefined
          }
          totalSteps={
            currentQuestion.answerType !== AnswerType.Info
              ? questions.filter((q) => q.answerType !== AnswerType.Info).length
              : undefined
          }
          onNavigate={async (index: number, questionId: string) => {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    currentQuestionId: questionId,
                  },
                  false,
                ),
              { showLoading: false },
            );

            setCurrentQuestionIndex(index);
            setCurrentQuestion(
              questions.find((q) => q.id === questionId) || null,
            );
          }}
        />

        <Style.MobileContentArea>
          <Style.QuestionNumber>
            {getDisplayQuestionNumber(currentQuestion, questions)}
          </Style.QuestionNumber>

          <Divider size="small" />

          <Style.MobileTitle $info={false}>
            {currentQuestion.question}
          </Style.MobileTitle>

          <Style.MobileDescription>
            {currentQuestion.information}
          </Style.MobileDescription>

          <Form
            form={form}
            component={false}
            onValuesChange={(changedValues: Record<string, unknown>) => {
              Object.entries(changedValues).forEach(
                async ([fieldName, value]) => {
                  if (value != null && value !== '') {
                    const questionId = fieldName;
                    const valueStr = value.toString();
                    setAnswersMap((prev) => {
                      const newMap = new Map(prev);
                      newMap.set(questionId, valueStr);
                      return newMap;
                    });

                    const question = questions.find((q) => q.id === questionId);

                    if (question) {
                      if (question.answerType === AnswerType.Textarea) {
                        debouncedSaveAnswerWrapper(questionId, valueStr);
                      } else if (question.answerType !== AnswerType.Info) {
                        const clearedChildQuestionIds = await saveAnswerOnly(
                          questionId,
                          valueStr,
                        );

                        if (clearedChildQuestionIds.length > 0) {
                          const fieldsToUpdate: Record<string, string> = {};
                          clearedChildQuestionIds.forEach(
                            (childQuestionId: string) => {
                              fieldsToUpdate[childQuestionId] = '';
                              setAnswersMap((prev) => {
                                const newMap = new Map(prev);
                                newMap.delete(childQuestionId);
                                return newMap;
                              });
                            },
                          );
                          form.setFieldsValue(fieldsToUpdate);
                        }
                      }
                    }
                  }
                },
              );
            }}
          >
            <Style.FormContainer>
              {currentQuestion.answerType === AnswerType.Radio && (
                <Form.Item
                  name={currentQuestion.id}
                  rules={[
                    {
                      required: currentQuestion.isRequired,
                      message: '此為必填欄位',
                    },
                  ]}
                  className="radio-in-form-item"
                >
                  <ScoreRadioGroup $color={getIndustryScoreColor(industry)} />
                </Form.Item>
              )}

              {currentQuestion.answerType === AnswerType.Textarea && (
                <Form.Item
                  name={currentQuestion.id}
                  rules={[
                    {
                      required: currentQuestion.isRequired,
                      message: '此為必填欄位',
                    },
                  ]}
                  className="textarea-in-form-item"
                >
                  <TextArea />
                </Form.Item>
              )}
            </Style.FormContainer>
          </Form>
        </Style.MobileContentArea>

        <Style.MobileButtonArea>
          <Style.MobileBackButtonWrapper>
            <Style.BackButton
              type="text"
              onClick={isNavigating ? undefined : handleBack}
              style={{
                visibility: currentQuestionIndex > 0 ? 'visible' : 'hidden',
                position: 'absolute',
                bottom: '17%',
                left: '8%',
              }}
            >
              <LeftOutlined />
              <span>BACK</span>
            </Style.BackButton>
          </Style.MobileBackButtonWrapper>

          <Style.MobileNextButtonWrapper>
            <ConfigProvider wave={{ disabled: true }}>
              <Style.NextButton
                onClick={isNavigating ? undefined : handleNext}
                $gradient={getIndustryButtonGradient(industry)}
              />
            </ConfigProvider>
            <Style.ButtonText>
              <span>NEXT</span>
              <RightOutlined />
            </Style.ButtonText>
          </Style.MobileNextButtonWrapper>
        </Style.MobileButtonArea>
      </Style.MobileContainer>
    );
  }

  // Desktop version - Info page
  return currentQuestion?.answerType === AnswerType.Info ? (
    <Style.BeforeSDGsStageContainer $bgImage={getBackgroundImage(industry)}>
      <Style.TopSection vertical>
        <Style.InfoTitle>
          SDGs自我評估問卷表
          <br />
          {industry}
        </Style.InfoTitle>

        <Style.ButtonContainer gap="16px">
          <OutlinedButton width="18rem" height="4rem" onClick={handleNext}>
            START THE QUESTIONNAIRE
          </OutlinedButton>
        </Style.ButtonContainer>
      </Style.TopSection>

      <ConfigProvider
        theme={{
          token: {
            colorSplit: '#f2f2f2',
          },
        }}
      >
        <Divider size="large" />
      </ConfigProvider>

      <Style.BottomSection justify="space-between" align="end">
        <Style.InfoLogoContainer vertical gap="16px">
          <Logo src={companyLogo} alt="Company Logo" height="40px" />
          <Logo src={unionLogo} alt="Union Logo" height="40px" />
        </Style.InfoLogoContainer>

        <Flex vertical gap={24}>
          <Style.AnswerExampleTitle>
            每個問題都使用0-5分制，其中：
          </Style.AnswerExampleTitle>
          <Flex gap={16}>
            {ANSWER_OPTIONS.map((option) => (
              <Flex key={option.key} vertical align="center" gap={20}>
                <Style.AnswerExampleRadio>
                  {option.value}
                </Style.AnswerExampleRadio>
                <Style.AnswerExampleText>
                  {option.label}
                </Style.AnswerExampleText>
              </Flex>
            ))}
          </Flex>
        </Flex>
      </Style.BottomSection>
    </Style.BeforeSDGsStageContainer>
  ) : (
    questions.length > 0 && currentQuestion && mounted && (
      <Style.QuestionnaireContainer
        $gradient={getIndustryBackgroundGradient(industry)}
      >
        <HamburgerMenu
          questions={questions}
          answersMap={answersMap}
          module={QuestionModule.SDGs}
          industry={industry}
          currentStep={
            questions
              .slice(0, currentQuestionIndex + 1)
              .filter((q) => q.answerType !== AnswerType.Info).length - 1
          }
          totalSteps={
            questions.filter((q) => q.answerType !== AnswerType.Info).length
          }
          onNavigate={async (index: number, questionId: string) => {
            await handleApiCall(
              () =>
                putQuestionnaire(
                  {
                    questionnaireId: questionnaireId!,
                    currentQuestionId: questionId,
                  },
                  false,
                ),
              { showLoading: false },
            );

            setCurrentQuestionIndex(index);
            setCurrentQuestion(
              questions.find((q) => q.id === questionId) || null,
            );
          }}
        />

        <ProgressBar
          currentStep={
            questions
              .slice(0, currentQuestionIndex + 1)
              .filter((q) => q.answerType !== AnswerType.Info).length - 1
          }
          totalSteps={
            questions.filter((q) => q.answerType !== AnswerType.Info).length
          }
          industry={industry}
        />

        <Style.QuestionnaireWrapper vertical justify="space-between">
          <div
            style={{
              display: 'grid',
              gridTemplateRows: 'auto 1fr',
              rowGap: '24px',
              flex: 1,
            }}
          >
            <Style.BackButton
              type="text"
              onClick={isNavigating ? undefined : handleBack}
              style={{
                visibility: currentQuestionIndex > 0 ? 'visible' : 'hidden',
              }}
            >
              <LeftOutlined />
              <span>BACK</span>
            </Style.BackButton>

            {currentQuestion && mounted && (
              <Style.SDGsStageContainer>
                <Flex vertical gap={16}>
                  <Style.Title />
                  <Flex gap="2rem">
                    <Style.QuestionNumber>
                      {getDisplayQuestionNumber(currentQuestion, questions)}
                    </Style.QuestionNumber>
                    <Flex vertical justify="center" gap={8}>
                      <Style.QuestionText>
                        {currentQuestion.question}
                      </Style.QuestionText>
                      {currentQuestion.information && (
                        <Style.QuestionInformation>
                          {currentQuestion.information}
                        </Style.QuestionInformation>
                      )}
                    </Flex>
                  </Flex>
                </Flex>

                <Form
                  form={form}
                  component={false}
                  onValuesChange={(changedValues) => {
                    Object.entries(changedValues).forEach(
                      async ([fieldName, value]) => {
                        if (value != null && value !== '') {
                          const questionId = fieldName;
                          const valueStr = value.toString();
                          setAnswersMap((prev) => {
                            const newMap = new Map(prev);
                            newMap.set(questionId, valueStr);
                            return newMap;
                          });

                          const question = questions.find(
                            (q) => q.id === questionId,
                          );

                          if (question) {
                            if (question.answerType === AnswerType.Textarea) {
                              debouncedSaveAnswerWrapper(questionId, valueStr);
                            } else if (
                              question.answerType !== AnswerType.Info
                            ) {
                              // 保存答案並獲取被清除的子問題ID
                              const clearedChildQuestionIds =
                                await saveAnswerOnly(questionId, valueStr);

                              // 如果後端清除了子問題答案，更新前端狀態
                              if (clearedChildQuestionIds.length > 0) {
                                const fieldsToUpdate: Record<string, string> =
                                  {};
                                clearedChildQuestionIds.forEach(
                                  (childQuestionId: string) => {
                                    fieldsToUpdate[childQuestionId] = '';
                                    setAnswersMap((prev) => {
                                      const newMap = new Map(prev);
                                      newMap.delete(childQuestionId);
                                      return newMap;
                                    });
                                  },
                                );
                                form.setFieldsValue(fieldsToUpdate);
                              }
                            }
                          }
                        }
                      },
                    );
                  }}
                >
                  <Style.FormContainer>
                    {currentQuestion.answerType === AnswerType.Radio && (
                      <Form.Item
                        name={currentQuestion.id}
                        rules={[
                          {
                            required: currentQuestion.isRequired,
                            message: '此為必填欄位',
                          },
                        ]}
                        className="radio-in-form-item"
                      >
                        <ScoreRadioGroup
                          $color={getIndustryScoreColor(industry)}
                        />
                      </Form.Item>
                    )}

                    {currentQuestion.answerType === AnswerType.Textarea && (
                      <Form.Item
                        name={currentQuestion.id}
                        rules={[
                          {
                            required: currentQuestion.isRequired,
                            message: '此為必填欄位',
                          },
                        ]}
                        className="textarea-in-form-item"
                      >
                        <TextArea />
                      </Form.Item>
                    )}
                  </Style.FormContainer>
                </Form>
              </Style.SDGsStageContainer>
            )}
          </div>

          <Style.LogoContainer vertical align="start" gap={16} $show={false}>
            <Logo src={companyLogo} alt="Company Logo" />
            <Logo src={unionLogo} alt="Union Logo" />
          </Style.LogoContainer>
        </Style.QuestionnaireWrapper>

        <ConfigProvider wave={{ disabled: true }}>
          <Style.NextButton
            onClick={isNavigating ? undefined : handleNext}
            $gradient={getIndustryButtonGradient(industry)}
          >
            <Style.ButtonTextWrapper>
              <Style.ButtonText>
                <span>NEXT</span>
                <RightOutlined />
              </Style.ButtonText>
            </Style.ButtonTextWrapper>
          </Style.NextButton>
        </ConfigProvider>
      </Style.QuestionnaireContainer>
    )
  );
};

export default SDGsModule;
